import { useEffect, useMemo, useReducer, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Box, Group, Text } from '@mantine/core';
import ResponsiveRow from '@/shared/responsive/ResponsiveRow';
import { useTopNavSlotNode } from '@/shell/TopNavSlot';
import { useBreakpointUp } from '@/shared/responsive/useBreakpoint';
import { container } from '@/container/CompositionRoot';
import { useGlobalFilters } from '@/features/global-filters/useGlobalFilters';
import { type GlobalFilterField } from '@/features/global-filters/GlobalFilterTypes';
import { globalScopeForPath } from '@/features/global-filters/GlobalFilterScope';
import { GLOBAL_FIELD_META, globalFieldGroups } from '@/features/global-filters/GlobalFilterFieldMeta';
import { GlobalFilterValueService } from '@/features/global-filters/GlobalFilterValueService';
import GlobalDateRangeControl from '@/features/global-filters/GlobalDateRangeControl';
import { providerIconForType } from '@/shared/ProviderIconComponent';
import type { AnalyticsField } from '@/shared/analytics/AnalyticsTypes';
import { assistFieldLabel, filterValueLabel } from '@/shared/analytics/MetricFormatting';
import { useFieldCatalog } from './FieldCatalogService';
import { useAssistFilters, useAssistSurface } from './assist/AssistSurfaceProvider';
import AddFilterMenu from './AddFilterMenu';
import ActiveFiltersChip, { type ActiveFilterEntry } from './ActiveFiltersChip';

const MAX_VALUES = 20;

/** Which section a local (page-scoped) analytics field sorts under in the Add-filter menu. */
const LOCAL_GROUP: Record<string, string> = {
    user_department: 'Org',
    user_title: 'Org',
    user_org_unit: 'Org',
    user_manager: 'Org',
    task_category: 'Activity',
    task_subcategory: 'Activity',
    difficulty: 'Activity',
    work_related: 'Activity',
    model: 'Activity',
    session_topic: 'Topics',
    topic_label: 'Topics',
};

interface PageLevelFilterRowProps {
    /** Renders the filter cluster visually present but non-interactive (e.g. governance tabs that ignore filters). */
    inactive?: boolean;
    /** Short note shown beside the controls when `inactive`. */
    inactiveNote?: string;
    /** The AI assist input, rendered at the bar's right end (stays interactive even when `inactive`). */
    assist?: ReactNode;
}

/**
 * The page's filtering and assist controls, ordered date → filters → AI
 * assist. On desktop the whole cluster portals into the shell's top nav (via
 * {@link useTopNavSlotNode}), right-aligned beside the account menu; on mobile
 * it renders as an inline band above the page title. It hosts the persistent
 * global filters (date range + provider/person) and, on analytics surfaces,
 * the page-scoped classifier filters — as the date chip, a compact
 * "Filters (n)" summary chip (its popover lists and edits what's applied), and
 * an Add-filter accordion, with the surface's assist input at the right end.
 * Each field is classified behind the scenes: global fields persist across
 * pages (via {@link useGlobalFilters}), local fields are surface-scoped (via
 * the {@link useAssistSurface} controller). Which global fields (and the date
 * control) show is decided by the route via {@link globalScopeForPath} — not
 * passed per call site. When rendered outside an analytics surface (e.g.
 * Licenses) only the global tier is shown.
 */
export default function PageLevelFilterRow({ inactive = false, inactiveNote, assist }: PageLevelFilterRowProps): ReactElement {
    const location = useLocation();
    const slotNode = useTopNavSlotNode();
    const isDesktop = useBreakpointUp('sm');
    const scope = useMemo(() => globalScopeForPath(location.pathname), [location.pathname]);
    const surface = useAssistSurface();
    const localFilters = useAssistFilters();
    const { controller: global, snapshot } = useGlobalFilters();
    const catalog = useFieldCatalog();
    const service = useMemo(() => container.resolve(GlobalFilterValueService), []);
    const [, forceUpdate] = useReducer((count: number) => count + 1, 0);

    useEffect(() => {
        void service.loadProviders().then(() => forceUpdate());
    }, [service]);

    const localEnabled = surface !== null;
    const globalSet = useMemo(() => new Set<string>(scope.fields), [scope]);
    const globalDims = snapshot.dimensions.filter((filter) => globalSet.has(filter.field));

    const localFieldOptions = useMemo(
        () => (localEnabled ? catalog.filter((field) => !globalSet.has(field.value)).map((field) => ({ value: field.value, label: field.label })) : []),
        [catalog, localEnabled, globalSet],
    );

    const localGroups = useMemo(() => {
        const groups: { group: string; fields: { value: string; label: string }[] }[] = [];
        for (const option of localFieldOptions) {
            const group = LOCAL_GROUP[option.value] ?? 'Other';
            const existing = groups.find((entry) => entry.group === group);
            if (existing) existing.fields.push(option);
            else groups.push({ group, fields: [option] });
        }
        return groups;
    }, [localFieldOptions]);

    const globalGroups = useMemo(() => globalFieldGroups().map((g) => ({ group: g.group, fields: g.fields.filter((f) => globalSet.has(f)) })).filter((g) => g.fields.length > 0), [globalSet]);

    const isGlobal = (field: string): field is GlobalFilterField => globalSet.has(field);

    const toggleGlobal = (field: GlobalFilterField, value: string, checked: boolean): void => {
        const current = globalDims.find((filter) => filter.field === field)?.values ?? [];
        const next = checked ? [...current, value] : current.filter((entry) => entry !== value);
        global.setFilter(field, next);
    };
    const toggleLocal = (field: AnalyticsField, value: string, checked: boolean): void => {
        if (surface === null) return;
        if (checked) {
            const current = localFilters.find((filter) => filter.field === field)?.values ?? [];
            if (current.length >= MAX_VALUES) return;
            surface.addFilters([{ field, values: [value] }]);
        } else {
            surface.removeValue(field, value);
        }
    };
    const clearAll = (): void => {
        scope.fields.forEach((field) => global.setFilter(field, []));
        surface?.clear();
    };

    const fieldLabel = (field: string): string => (isGlobal(field) ? GLOBAL_FIELD_META[field as GlobalFilterField].label : assistFieldLabel(field));
    const valueLabel = (field: string, value: string): string => (field === 'provider_connection_id' ? service.providerLabel(value) : filterValueLabel(field, value));
    const valueIcon = (field: string, value: string): ReactElement | null => {
        if (field !== 'provider_connection_id') return null;
        const typeId = service.providerTypeId(value);
        return typeId !== null ? providerIconForType(typeId, 16) : null;
    };

    const entries: ActiveFilterEntry[] = [
        ...globalDims.map((filter) => ({ field: filter.field, label: fieldLabel(filter.field), global: true, values: filter.values })),
        ...(localEnabled ? localFilters.map((filter) => ({ field: filter.field, label: fieldLabel(filter.field), global: false, values: filter.values })) : []),
    ];

    const chipToggle = (entry: ActiveFilterEntry, value: string, checked: boolean): void => {
        if (entry.global) toggleGlobal(entry.field as GlobalFilterField, value, checked);
        else toggleLocal(entry.field as AnalyticsField, value, checked);
    };

    const filterCluster = (wrap: 'wrap' | 'nowrap'): ReactElement => (
        <Group gap="xs" wrap={wrap} align="center" style={{ minWidth: 0, ...(inactive ? { opacity: 0.55, pointerEvents: 'none' as const } : {}) }}>
            {inactive && inactiveNote !== undefined && (
                <Text size="xs" c="dimmed">
                    {inactiveNote}
                </Text>
            )}
            {scope.dateRange && <GlobalDateRangeControl range={snapshot.dateRange} onChange={(range) => global.setDateRange(range)} />}
            {entries.length > 0 && <ActiveFiltersChip entries={entries} valueLabel={valueLabel} valueIcon={valueIcon} onToggle={chipToggle} onClearAll={clearAll} />}
            <AddFilterMenu
                globalGroups={globalGroups}
                localGroups={localGroups}
                globalDims={globalDims}
                localFilters={localEnabled ? localFilters : []}
                onToggleGlobal={toggleGlobal}
                onToggleLocal={toggleLocal}
                emptyLabel={entries.length === 0 ? 'Showing all' : undefined}
            />
        </Group>
    );

    if (slotNode !== null && isDesktop) {
        return createPortal(
            <Group gap="sm" wrap="nowrap" align="center" justify="flex-end" style={{ minWidth: 0 }}>
                {filterCluster('nowrap')}
                {assist !== undefined && <Box style={{ flexShrink: 1, minWidth: 0 }}>{assist}</Box>}
            </Group>,
            slotNode,
        );
    }

    return (
        <Box mb="lg" pb="sm" style={{ borderBottom: '1px solid var(--bm-color-border)' }}>
            <ResponsiveRow gap="sm" align={{ base: 'stretch', sm: 'center' }}>
                <Box style={{ minWidth: 0, flex: 1 }}>{filterCluster('wrap')}</Box>
                {assist !== undefined && <Box style={{ flexShrink: 0, minWidth: 0 }}>{assist}</Box>}
            </ResponsiveRow>
        </Box>
    );
}
